import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Analytics as AnalyticsData, type GroupStat } from '../api/client';
import { useLang } from '../i18n/lang';

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const pct = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;

// Win-rate & competitive analytics (flywheel #2).
export function Analytics() {
  const { t } = useLang();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getAnalytics()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="muted">{t({ en: 'Loading analytics…', fil: 'Naglo-load ng analytics…' })}</div>;
  if (error) return <div className="error">{error}</div>;
  if (!data) return null;

  const o = data.overall;
  const decided = o.won + o.lost;

  return (
    <div>
      <h1>Win/Loss Analytics</h1>
      <p className="muted" style={{ marginTop: 0 }}>{t({ en: 'Learns from the outcomes you record on the Dashboard. The more bids you mark Won/Lost, the sharper this gets.', fil: 'Natututo mula sa mga outcome na ni-record mo sa Dashboard. Habang mas maraming bid ang mina-mark mong Won/Lost, mas tumatalas ito.' })}</p>

      {decided === 0 && (
        <div className="card">
          <h2>{t({ en: 'No decided bids yet', fil: 'Wala pang decided na bid' })}</h2>
          <p className="muted">{t({ en: 'Record', fil: 'I-record ang' })} <strong>Won</strong> {t({ en: 'or', fil: 'o' })} <strong>Lost</strong> {t({ en: 'on your bids (Dashboard → Outcome) and your win rate, competitive pricing gap and per-estimator/customer trends appear here.', fil: 'sa iyong mga bid (Dashboard → Outcome) at lalabas dito ang iyong win rate, competitive pricing gap at mga trend per-estimator/customer.' })}</p>
          <Link className="btn btn-primary" to="/">{t({ en: 'Go to Dashboard', fil: 'Pumunta sa Dashboard' })}</Link>
        </div>
      )}

      {decided > 0 && (
        <>
          {/* Overall */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <Stat label={t({ en: 'Win rate', fil: 'Win rate' })} value={o.winRate == null ? '—' : `${o.winRate}%`} sub={`${o.won} won / ${o.lost} lost`} accent="var(--teal)" />
            <Stat label={t({ en: 'Won value', fil: 'Won value' })} value={money(o.wonValue)} sub={`${o.won} awarded`} accent="var(--gold-dark)" />
            <Stat label={t({ en: 'Decided bids', fil: 'Decided na bid' })} value={String(decided)} sub={`${o.open} open · ${o.noBid} no-bid`} />
            <Stat label={t({ en: 'Total bids', fil: 'Total na bid' })} value={String(o.bids)} sub={t({ en: 'all time', fil: 'lahat ng panahon' })} />
          </div>

          {/* Competitive pricing gap */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h2 style={{ marginTop: 0 }}>{t({ en: 'Pricing vs the market', fil: 'Pricing vs the market' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— how our bid compared to the winning number on losses', fil: '— kung paano ang bid natin kumpara sa winning number sa mga losses' })}</span></h2>
            {data.lossGap.count === 0
              ? <p className="muted">{t({ en: 'Record the', fil: 'I-record ang' })} <strong>winning bid $</strong> {t({ en: 'on lost bids (Outcome → "winning bid $") to see how far above the market we were.', fil: 'sa mga lost na bid (Outcome → "winning bid $") para makita kung gaano kalayo tayo sa market.' })}</p>
              : (
                <>
                  <p style={{ marginTop: 0 }}>
                    {t({ en: 'On', fil: 'Sa' })} <strong>{data.lossGap.count}</strong> lost bid{data.lossGap.count > 1 ? 's' : ''} {t({ en: 'where we know the winning number, we averaged', fil: 'kung saan alam natin ang winning number, nag-average tayo ng' })}{' '}
                    <strong style={{ color: (data.lossGap.avgPctOver ?? 0) > 0 ? 'var(--danger)' : 'var(--teal)' }}>{pct(data.lossGap.avgPctOver ?? 0)}</strong>{' '}
                    {t({ en: 'versus the winner.', fil: 'kumpara sa winner.' })} {(data.lossGap.avgPctOver ?? 0) > 8 ? t({ en: 'That suggests room to sharpen pricing on competitive work.', fil: 'Ipinapahiwatig nito na may puwang para patalasin ang pricing sa competitive na trabaho.' }) : t({ en: 'That is close — losses are likely about factors other than price.', fil: 'Malapit na iyan — malamang na ang mga losses ay tungkol sa ibang factor maliban sa presyo.' })}
                  </p>
                  <table>
                    <thead><tr><th>Bid</th><th>{t({ en: 'Won by', fil: 'Won by' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'Our bid', fil: 'Our bid' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'Winning', fil: 'Winning' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'Over/under', fil: 'Over/under' })}</th></tr></thead>
                    <tbody>
                      {data.lossGap.samples.map((s, i) => (
                        <tr key={i}>
                          <td>{s.name}</td><td className="muted">{s.competitor ?? '—'}</td>
                          <td style={{ textAlign: 'right' }}>{money(s.ourBid)}</td>
                          <td style={{ textAlign: 'right' }}>{money(s.winning)}</td>
                          <td style={{ textAlign: 'right', color: s.pctOver > 0 ? 'var(--danger)' : 'var(--teal)', fontWeight: 600 }}>{pct(s.pctOver)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
          </div>

          {/* By estimator / customer */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <GroupTable title={t({ en: 'Win rate by estimator', fil: 'Win rate by estimator' })} rows={data.byEstimator} col={t({ en: 'Estimator', fil: 'Estimator' })} />
            <GroupTable title={t({ en: 'Win rate by customer', fil: 'Win rate by customer' })} rows={data.byCustomer} col={t({ en: 'Customer', fil: 'Customer' })} />
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ flex: 1, minWidth: 150, border: '1px solid var(--gray-100)', borderRadius: 8, padding: '12px 16px' }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent || 'var(--ink, #111)' }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

function GroupTable({ title, rows, col }: { title: string; rows: GroupStat[]; col: string }) {
  const { t } = useLang();
  return (
    <div className="card" style={{ flex: 1, minWidth: 320 }}>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      {rows.length === 0 ? <p className="muted">{t({ en: 'No decided bids yet.', fil: 'Wala pang decided na bid.' })}</p> : (
        <table>
          <thead><tr><th>{col}</th><th style={{ textAlign: 'right' }}>Bids</th><th style={{ textAlign: 'right' }}>{t({ en: 'Won', fil: 'Won' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'Win rate', fil: 'Win rate' })}</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.name}</td>
                <td style={{ textAlign: 'right' }}>{r.decided}</td>
                <td style={{ textAlign: 'right' }}>{r.won}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: r.winRate >= 50 ? 'var(--teal)' : 'var(--gray-500, #888)' }}>{r.winRate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
